import * as React from "react";
import { UpdateFormTemplateProc } from "@/templates/FormTemplate";
import { DocumentsView } from "./DocumentsView";
import {
  ExpressFormMenuKey,
  getExpressSectionSourceDataByKey,
  putExpressSectionDataByKey,
} from "./express-data";
import { ExpressUiInfoContext } from "@/store/ExpressUiInfo";
import {
  DocumentErrors,
  ExpressDocumentsRecord,
  FileTags,
  ServiceProvider,
  UploadedDocument,
} from "@/types";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import {
  isGOROptional,
  isGrantOfRepresentationRequired,
  isPOBARequiredExpress,
  isWillRequiredExpressForm,
} from "@/helpers/documents-helpers";
import { createExpressDocument } from "@/services/api/document-api";

const currentSection = ExpressFormMenuKey.Documents;

export type DocumentsState = {
  readonly record: ExpressDocumentsRecord;
  readonly errors: DocumentErrors;
  readonly hasChanges: boolean;
};

const serverRecordFromRecord = (newIds: ExpressDocumentsRecord) => {
  return {
    proofOfAddressIds: (newIds.proofOfAddress || []).map((d) => d.id),
    deathCertificateIds: (newIds.proofOfDeath || []).map((d) => d.id),
    willAndProbateIds: (newIds.willAndProbate || []).map((d) => d.id),
    grantOfRepresentationIds: (newIds.grantOfRepresentation || []).map((d) => d.id),
    proofOfBankAccountIds: (newIds.proofOfBankAccount || []).map((d) => d.id),
  };
};

export type DocumentsProps = {
  serviceProvider: Partial<ServiceProvider>;
  busy: boolean;
  remoteError: string | undefined;
  onSectionClick: (section: string) => void;
  updateTemplate: UpdateFormTemplateProc;
  removeRemoteError: () => void;
  readonly next: () => void;
};

export const Documents: React.FC<DocumentsProps> = ({
  serviceProvider,
  busy,
  remoteError,
  onSectionClick,
  updateTemplate,
  removeRemoteError,
  next,
}) => {
  useScrollToTop();
  const {
    state: { willExists, quilter, notifierRole },
  } = React.useContext(ExpressUiInfoContext);

  const [{ record, errors, hasChanges }, setState] = React.useState<DocumentsState>({
    record: {
      proofOfAddress: [],
      proofOfDeath: [],
      willAndProbate: [],
      grantOfRepresentation: [],
      proofOfBankAccount: [],
    },
    hasChanges: false,
    errors: {},
  });

  const askForGrantOfRepresentation = React.useMemo(
    () => isGrantOfRepresentationRequired(quilter, !!serviceProvider.grantOfRepresentationNeeded),
    [quilter, serviceProvider.grantOfRepresentationNeeded]
  );
  const showAGOROptional: boolean = React.useMemo(
    () => isGOROptional(quilter, !!serviceProvider.grantOfRepresentationNeeded),
    [quilter, serviceProvider.grantOfRepresentationNeeded]
  );

  const askForProofOfBankAccount = React.useMemo(
    () => isPOBARequiredExpress(quilter, notifierRole),
    [quilter, notifierRole]
  );
  const uploaderFor = (name: keyof ExpressDocumentsRecord) => (files: ReadonlyArray<File>) => {
    // TODO set busy

    const tags = {
      proofOfAddress: [FileTags.ProofOfAddress],
      proofOfDeath: [FileTags.ProofOfDeath],
      willAndProbate: [FileTags.Will],
      grantOfRepresentation: [FileTags.GrantOfRepresentation],
      proofOfBankAccount: [FileTags.ProofOfBankAccount],
    }[name];

    const uploads = files.map((file) => {
      return createExpressDocument({ file, fileTags: tags }).then(
        (uploadedDocument: UploadedDocument) => {
          setState((s) => ({
            ...s,
            errors: {},
          }));
          return uploadedDocument;
        },
        (error) => {
          setState((s) => ({
            ...s,
            errors: error,
          }));
          return null;
        }
      );
    });

    Promise.all(uploads).then((uploads) => {
      uploads = uploads.filter((u) => u !== null);
      // TODO: unset busy
      setState((s) => ({
        ...s,
        hasChanges: true,
        record: {
          ...s.record,
          [name]: [...(s.record[name] || []), ...uploads],
        },
      }));
    });
  };

  const removerFor = (name: keyof ExpressDocumentsRecord) => {
    return (id: string) => {
      setState((s) => ({
        ...s,
        hasChanges: true,
        record: {
          ...s.record,
          [name]: s.record[name].filter((d) => d.id !== id),
        },
      }));
    };
  };

  const updateProofOfAddress = React.useCallback(uploaderFor("proofOfAddress"), [
    record.proofOfAddress,
    setState,
  ]);

  const updateProofOfDeath = React.useCallback(uploaderFor("proofOfDeath"), [
    record.proofOfDeath,
    setState,
  ]);

  const updateWillAndProbate = React.useCallback(uploaderFor("willAndProbate"), [
    record.willAndProbate,
    setState,
  ]);

  const updateGrantOfRepresentation = React.useCallback(uploaderFor("grantOfRepresentation"), [
    record.grantOfRepresentation,
    setState,
  ]);

  const updateProofOfBankAccount = React.useCallback(uploaderFor("proofOfBankAccount"), [
    record.proofOfBankAccount,
    setState,
  ]);

  const removeProofOfAddress = React.useCallback(removerFor("proofOfAddress"), [setState]);
  const removeProofOfDeath = React.useCallback(removerFor("proofOfDeath"), [setState]);
  const removeWillAndProbate = React.useCallback(removerFor("willAndProbate"), [setState]);
  const removeGrantOfRepresentation = React.useCallback(removerFor("grantOfRepresentation"), [
    setState,
  ]);
  const removeProofOfBankAccount = React.useCallback(removerFor("proofOfBankAccount"), [setState]);

  const setError = React.useCallback(
    (name: string, message: string) => {
      setState((s) => ({
        ...s,
        hasChanges: true,
        record: { ...s.record, [name]: [] },
        errors: { ...s.errors, [name]: message },
      }));
    },
    [setState]
  );

  React.useEffect(() => {
    const data = getExpressSectionSourceDataByKey(currentSection);

    if (!data) {
      setState(({ record }) => ({ record, hasChanges: false, errors: {} }));
      return;
    }

    setState({ record: data, hasChanges: false, errors: {} });
  }, [setState]);

  const onNext = React.useCallback(() => {
    const errors = errorsOf(
      record,
      serviceProvider,
      willExists,
      askForProofOfBankAccount,
      askForGrantOfRepresentation
    );

    if (errors) {
      setState({
        hasChanges,
        record,
        errors,
      });
      putExpressSectionDataByKey(currentSection, { source: record });
      return;
    }

    if (!hasChanges) {
      next();
      return;
    }

    putExpressSectionDataByKey(currentSection, {
      source: record,
      target: serverRecordFromRecord(record),
    });
    next();
  }, [
    record,
    serviceProvider,
    hasChanges,
    next,
    willExists,
    askForProofOfBankAccount,
    askForGrantOfRepresentation,
  ]);

  const onSectionClickExtended = React.useCallback(
    (section) => {
      if (hasChanges) {
        const errors = errorsOf(
          record,
          serviceProvider,
          willExists,
          askForProofOfBankAccount,
          !!serviceProvider.grantOfRepresentationNeeded
        );
        putExpressSectionDataByKey(currentSection, {
          source: record,
          target: errors ? undefined : serverRecordFromRecord(record),
        });
      }

      onSectionClick(section);
    },
    [record, serviceProvider, hasChanges, onSectionClick, willExists]
  );

  return (
    <DocumentsView
      serviceProvider={serviceProvider}
      askForProofOfAddress={serviceProvider.proofOfAddressNeeded}
      askForWillAndProbate={isWillRequiredExpressForm(
        serviceProvider as ServiceProvider,
        willExists
      )}
      askForGrantOfRepresentation={askForGrantOfRepresentation}
      askForProofOfBankAccount={askForProofOfBankAccount}
      busy={busy}
      record={record}
      errors={errors}
      hasChanges={hasChanges}
      remoteError={remoteError}
      setError={setError}
      updateProofOfAddress={updateProofOfAddress}
      removeProofOfAddress={removeProofOfAddress}
      updateWillAndProbate={updateWillAndProbate}
      removeWillAndProbate={removeWillAndProbate}
      updateGrantOfRepresentation={updateGrantOfRepresentation}
      removeGrantOfRepresentation={removeGrantOfRepresentation}
      updateProofOfBankAccount={updateProofOfBankAccount}
      removeProofOfBankAccount={removeProofOfBankAccount}
      updateProofOfDeath={updateProofOfDeath}
      removeProofOfDeath={removeProofOfDeath}
      onNext={onNext}
      onSectionClick={onSectionClickExtended}
      updateTemplate={updateTemplate}
      removeRemoteError={removeRemoteError}
      showAGOROptional={showAGOROptional}
    />
  );
};

const errorsOf = (
  record: ExpressDocumentsRecord,
  serviceProvider: any,
  collectWill: boolean,
  askForProofOfBankAccount: boolean,
  askForGrantOfRepresentation: boolean
): DocumentErrors | undefined => {
  const errors = {} as DocumentErrors;

  if (serviceProvider.deathCertificateNeeded && record?.proofOfDeath.length === 0) {
    errors.proofOfDeath = "required";
  }
  const askForWillAndProbate = isWillRequiredExpressForm(serviceProvider, collectWill);

  if (askForWillAndProbate && record?.willAndProbate && record?.willAndProbate.length === 0) {
    errors.willAndProbate = "Required field";
  }

  if (
    askForGrantOfRepresentation &&
    record?.grantOfRepresentation &&
    record?.grantOfRepresentation.length === 0
  ) {
    errors.grantOfRepresentation = "Required field";
  }

  if (
    askForProofOfBankAccount &&
    record?.proofOfBankAccount &&
    record?.proofOfBankAccount.length === 0
  ) {
    errors.proofOfBankAccount = "Required field";
  }

  return Object.keys(errors).length === 0 ? undefined : errors;
};
